/**
 * Calibration — T10 of the Performance Reviews plan
 * (`docs/plans/performance-management/performance-reviews-plan.md`, Calibration gap-analysis
 * section). One flat router, `calibrationContract` (`/calibration-sessions`).
 *
 * `CalibrationAdjustment` IS APPEND-ONLY. `submitAdjustment` never overwrites
 * `ReviewCompetencyRating.selfRating`/`managerRating`, and old adjustment rows are never deleted, so
 * `listAdjustments` always shows the whole history: original rating, every adjustment, in order.
 * See the plan's Data model section on why `overallScore` (computed) and `finalRating` (calibrated)
 * stay separate columns forever.
 *
 * AN ADJUSTMENT TAKES EFFECT ONLY ON THE HR COUNTERSIGN (perf remediation #13, T2.9).
 * `submitAdjustment` writes a PENDING row (`appliedAt: null`) and leaves
 * `PerformanceReview.finalRating` alone; its `fromRating` is the value the review would have with
 * this session's earlier pending adjustments applied. The countersign's terminal approval applies,
 * per review, the session's latest pending adjustment to `finalRating` and stamps its `appliedAt` —
 * all or nothing, refused with a 409 listing the reviews another countersign changed meanwhile. A
 * rejected countersign applies nothing; its adjustments stay pending (and on record).
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  commentOnlyDecisionBodySchema,
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  ratingValueSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const calibrationScopeSchema = z.enum(['team', 'department', 'org']);
export type CalibrationScope = z.infer<typeof calibrationScopeSchema>;
// 'pending_finalization' is the workflow-gated gap between "facilitator wants to finalize" and
// "finalized" — finalizeSession starts a calibration_session WorkflowInstance (HR countersign)
// rather than finalizing directly, since finalRating is compensation-adjacent. See calibration.ts.
// 'cancelled' (perf remediation T2.11 follow-up): an abandoned draft/in_review session withdrawn by
// its facilitator or HR — terminal, nothing applied, and it no longer holds the cycle's release.
export const calibrationSessionStatusSchema = z.enum([
  'draft',
  'in_review',
  'pending_finalization',
  'finalized',
  'cancelled',
]);
export type CalibrationSessionStatus = z.infer<typeof calibrationSessionStatusSchema>;

export const calibrationSessionSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  cycleId: uuidSchema,
  scope: calibrationScopeSchema,
  scopeId: uuidSchema.nullable(),
  facilitatorPersonId: uuidSchema,
  status: calibrationSessionStatusSchema,
  workflowInstanceId: uuidSchema.nullable(),
  createdAt: instantSchema,
});
export type CalibrationSession = z.infer<typeof calibrationSessionSchema>;

export const calibrationAdjustmentSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  sessionId: uuidSchema,
  reviewId: uuidSchema,
  personName: z.string(),
  fromRating: ratingValueSchema.nullable(),
  toRating: ratingValueSchema,
  reason: z.string(),
  adjustedByPersonId: uuidSchema,
  /** When the HR countersign wrote `toRating` to the review's `finalRating`. Null while pending —
   * and forever for an adjustment a later one in the same session replaced, or whose session was
   * rejected and never re-approved. */
  appliedAt: instantSchema.nullable(),
  createdAt: instantSchema,
});
export type CalibrationAdjustment = z.infer<typeof calibrationAdjustmentSchema>;

/** Why the countersign refused to apply (the 409's `details`): these reviews' `finalRating` was
 * changed by another calibration countersign after this session adjusted them (or the review left
 * `manager_reviewed`). Nothing was applied; HR rejects the countersign and the facilitator
 * re-adjusts against the current value. */
export const calibrationApplyConflictSchema = errorBodySchema.extend({
  details: z
    .object({
      conflictingReviews: z.array(
        z.object({ reviewId: uuidSchema, personId: uuidSchema, personName: z.string() }),
      ),
    })
    .optional(),
});
export type CalibrationApplyConflict = z.infer<typeof calibrationApplyConflictSchema>;

export const listSessionsQuerySchema = paginationQuerySchema.extend({
  cycleId: uuidSchema.optional(),
  status: calibrationSessionStatusSchema.optional(),
});

export const createSessionBodySchema = z.object({
  cycleId: uuidSchema,
  scope: calibrationScopeSchema,
  scopeId: uuidSchema.optional(),
});

export const submitAdjustmentBodySchema = z.object({
  reviewId: uuidSchema,
  toRating: ratingValueSchema,
  reason: z.string().trim().min(1).max(2000),
});

export const decideCalibrationBodySchema = commentOnlyDecisionBodySchema;

/** Withdraw an abandoned session. The reason is required: it goes into the audit log. */
export const cancelSessionBodySchema = z.object({
  reason: z.string().trim().min(1).max(2000),
});
export type CancelSessionBody = z.infer<typeof cancelSessionBodySchema>;

// ---------------------------------------------------------------------------
// Server-side bucketing + suggested normalisation (PRF-12)
// ---------------------------------------------------------------------------

/** One band of a session's live distribution. `targetPercent`/`tolerance` are `null` when the
 * cycle's `ratingBands` (the calibration COHORT-distribution target — never the `scoreBands`
 * score->label mapping, see `review-cycle.contract.ts`'s header) has no entry matching this band's
 * name; `outOfBand` is then always `false` — there is nothing to be out of. */
export const calibrationBandDistributionSchema = z.object({
  band: z.string(),
  count: z.number().int(),
  percent: z.string(),
  targetPercent: z.string().nullable(),
  tolerance: z.string().nullable(),
  outOfBand: z.boolean(),
});
export type CalibrationBandDistribution = z.infer<typeof calibrationBandDistributionSchema>;

export const getDistributionResponseSchema = z.object({
  bands: z.array(calibrationBandDistributionSchema),
  /** Reviews with a usable score (`finalRating ?? overallScore`) in the session's scope. */
  totalRated: z.number().int(),
  /** Every review in the session's scope, rated or not — lets the UI show "12 of 20 rated". */
  totalScoped: z.number().int(),
});

/** One proposed (never applied) rating nudge — `PROPOSES adjustments... each accepted/rejected
 * individually... nothing automatic` (the plan's own wording). Accepting one is an ordinary
 * `submitAdjustment` call with this `reviewId`/`toRating`/`reason`; rejecting one needs no API call
 * at all — it is simply discarded client-side, since nothing was ever written for it. There is
 * therefore no separate "suggestion" row or id: this response is a stateless, recomputed-on-demand
 * proposal, not a stored entity. */
export const calibrationSuggestionSchema = z.object({
  reviewId: uuidSchema,
  personName: z.string(),
  fromRating: ratingValueSchema,
  toRating: ratingValueSchema,
  fromBand: z.string(),
  toBand: z.string(),
  reason: z.string(),
});
export type CalibrationSuggestion = z.infer<typeof calibrationSuggestionSchema>;

export const suggestNormalisationResponseSchema = z.object({
  suggestions: z.array(calibrationSuggestionSchema),
});

export const calibrationContract = c.router(
  {
    listSessions: {
      method: 'GET',
      path: '/',
      query: listSessionsQuerySchema,
      responses: {
        200: paginatedSchema(calibrationSessionSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'List calibration sessions — review.calibrate:execute',
      metadata: routeMeta(null),
    },

    getSession: {
      method: 'GET',
      path: '/:sessionId',
      responses: { 200: calibrationSessionSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: 'Get one calibration session',
      metadata: routeMeta('review.calibrate:execute'),
    },

    createSession: {
      method: 'POST',
      path: '/',
      body: createSessionBodySchema,
      responses: {
        201: calibrationSessionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** The cycle is not `in_calibration` (draft, still active, or closed). */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Open a calibration session over a cycle scope — review.calibrate:execute',
      metadata: routeMeta('review.calibrate:execute'),
    },

    startReview: {
      method: 'POST',
      path: '/:sessionId/start-review',
      body: z.object({}),
      responses: {
        200: calibrationSessionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Move a session from draft to in_review',
      metadata: routeMeta('review.calibrate:execute'),
    },

    getDistribution: {
      method: 'GET',
      path: '/:sessionId/distribution',
      responses: {
        200: getDistributionResponseSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Live rating distribution for this session, bucketed server-side by the cycle\'s real scoreBands and filtered to the session\'s own scope (PRF-12). Counts each review at its value with THIS session\'s pending adjustments applied',
      metadata: routeMeta('review.calibrate:execute'),
    },

    suggestNormalisation: {
      method: 'POST',
      path: '/:sessionId/suggest-normalisation',
      body: z.object({}),
      responses: {
        200: suggestNormalisationResponseSchema,
        401: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Proposes rating nudges toward each band\'s target distribution — never applies anything; accept one via submitAdjustment, reject by discarding it (PRF-12)',
      metadata: routeMeta('review.calibrate:execute'),
    },

    listAdjustments: {
      method: 'GET',
      path: '/:sessionId/adjustments',
      query: paginationQuerySchema,
      responses: {
        200: paginatedSchema(calibrationAdjustmentSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'The full append-only audit trail for one session',
      metadata: routeMeta('review.calibrate:execute'),
    },

    submitAdjustment: {
      method: 'POST',
      path: '/:sessionId/adjustments',
      body: submitAdjustmentBodySchema,
      responses: {
        201: calibrationAdjustmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Append a PENDING calibration adjustment (appliedAt null) — PerformanceReview.finalRating changes only when the HR countersign approves the session; never overwrites selfRating/managerRating',
      metadata: routeMeta('review.calibrate:execute'),
    },

    finalizeSession: {
      method: 'POST',
      path: '/:sessionId/finalize',
      body: z.object({}),
      responses: {
        200: calibrationSessionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Starts the HR-countersign WorkflowInstance for this session (calibration_session)',
      metadata: routeMeta('review.calibrate:execute'),
    },

    approveFinalization: {
      method: 'POST',
      path: '/:sessionId/approve',
      body: decideCalibrationBodySchema,
      responses: {
        200: calibrationSessionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: calibrationApplyConflictSchema,
        412: errorBodySchema,
      },
      summary:
        "HR countersign — on the workflow's terminal approval applies each review's latest pending adjustment to finalRating and finalizes the session; 409 (details.conflictingReviews, nothing applied) when another countersign changed one of those reviews meanwhile; 403 for the session's own facilitator (nobody countersigns their own calibration)",
      metadata: routeMeta(null),
    },

    rejectFinalization: {
      method: 'POST',
      path: '/:sessionId/reject',
      body: decideCalibrationBodySchema,
      responses: {
        200: calibrationSessionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary:
        "HR sends the session back to in_review — nothing is applied, the adjustments stay pending, the facilitator is notified, and more adjustments are accepted again; 403 for the session's own facilitator",
      metadata: routeMeta(null),
    },

    cancelSession: {
      method: 'POST',
      path: '/:sessionId/cancel',
      body: cancelSessionBodySchema,
      responses: {
        200: calibrationSessionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not draft / in_review — a session awaiting countersign is rejected back to review
         * first; a finalized or cancelled one is settled. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Withdraw an abandoned draft or in_review session — its facilitator (review.calibrate:execute over the session) or HR (review.cycle:manage). Terminal: its pending adjustments never apply and it no longer blocks the cycle's release; the reason is audited. 409 while awaiting countersign (reject it first) or once settled. Requires Idempotency-Key",
      metadata: routeMeta(['review.calibrate:execute', 'review.cycle:manage']),
    },
  },
  { pathPrefix: '/calibration-sessions' },
);
